<template>
	<div style="display: flex; flex-direction: column; gap: 40px">
		<Card>
			<CardGrafico
				:loading="dashboardStore.getters.isEmpty"
				:title="$t('overview')"
				:subtitle="`Última atualização em ${new Date(ultimaMedicao.value).toLocaleDateString(
					'pt-BR',
					{
						month: 'long',
						day: 'numeric',
						year: 'numeric',
					},
				)}`"
			>
				<template #botoes>
					<DropdownCheckbox
						v-model="fazendasAtivas"
						:value="fazendasOptions"
						:label="$t('pickFarm')"
						style="width: fit-content; padding: 1rem 0"
					/>
				</template>
				<template #grafico>
					<div style="display: flex; justify-content: space-around">
						<KPI
							:value="fazendasEscolhidas.length || fazendasOptions.length"
							:description="$t('farms')"
						/>
						<KPI
							:value="dendrometros.length"
							:description="$t('dendrometro')"
						/>
						<KPI
							:value="`${areaFazendas.toFixed(2)} ha`"
							:description="$t('areaTotalFazenda')"
						/>
						<KPI
							:value="`${areaPlantios.toFixed(2)} ha`"
							:description="$t('areaTotalPlantio')"
						/>
						<KPI
							:value="`${volumeMedioPorFazenda.toFixed(2)} m³/ha`"
							:description="$t('volMedioFazenda')"
						/>
					</div>
				</template>
			</CardGrafico>
		</Card>
		<Card>
			<CardGrafico
				:loading="dashboardStore.getters.isEmpty"
				:title="$t('productivityAnalysis')"
				:subtitle="`Última atualização em ${new Date(ultimaMedicao.value).toLocaleDateString(
					'pt-BR',
					{
						month: 'long',
						day: 'numeric',
						year: 'numeric',
					},
				)}`"
			>
				<template #grafico>
					<div style="display: flex; gap: 1rem">
						<ButtonDropdown
							v-show="dashboardStore.state.customizada"
							v-model="unidade"
							:options="unidadeOptions"
							style="width: fit-content; padding: 1rem 0"
						/>
						<ButtonDropdown
							v-show="dashboardStore.state.customizada"
							v-model="showBy"
							:options="optionsShowBy"
							style="width: fit-content; padding: 1rem 0"
						/>
					</div>
					<Chart
						v-if="dashboardStore.state.customizada"
						ref="ref__chart"
						class="container__graficomedicao__grafico"
						:options="chartOptions"
					/>
					<div
						v-else
						style="width: 100%; display: flex; justify-content: center; align-items: center"
					>
						<ButtonDropdown
							v-model="dashboardStore.state.customizada"
							:options="
								variaveisCustomizadasOptions.map((m) => {
									return { value: m, label: m }
								})
							"
							:label="$t('configureUmaVariavel')"
						/>
					</div>
				</template>
			</CardGrafico>
		</Card>
	</div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { sumBy, mean, meanBy, orderBy, groupBy, sortBy } from 'lodash'
import { Chart } from 'highcharts-vue'
import Card from '../../../components/Card/Card.vue'
import CardGrafico from '../../../components/CardGrafico/CardGrafico.vue'
import KPI from '../../../components/KPI/KPI.vue'
import ButtonDropdown from '../../../components/ButtonDropdown/ButtonDropdown.vue'
import DropdownCheckbox from '../../../components/DropdownCheckbox/DropdownCheckbox.vue'
import { useStore } from '../../../store/modules/moduleDashboardAnalytics'
import { getDropdownEstratosFlorestais } from '../../../services/requests/EstratoFlorestalRequests'
import i18n from '../../../VueI18n'

const dashboardStore = useStore()

// Mapeamento de estratos (ID -> Nome)
const estratosMap = ref({})
const estratosLoaded = ref(false)

// Carregar estratos imediatamente
const carregarEstratos = async () => {
	try {
		const estratos = await getDropdownEstratosFlorestais()
		const novoMap = {}
		estratos.forEach((estrato) => {
			novoMap[estrato.id] = `EF-${estrato.esfl_cd_codigo} - ${estrato.esfl_ds_descricao}`
		})
		estratosMap.value = novoMap
		estratosLoaded.value = true
	} catch (error) {
		console.error('Erro ao carregar estratos:', error)
		estratosLoaded.value = true
	}
}

// Inicia o carregamento imediatamente
carregarEstratos()
const showBy = ref('customizada')
const unidade = ref('volumeComCasca')
const unidadeOptions = ref([
	{ value: 'dap', label: 'DAP' },
	{ value: 'ht', label: 'HT' },
	{ value: 'volumeComCasca', label: i18n.t('volume') },
])

const optionsShowBy = ref([
	{ value: 'customizada', label: dashboardStore.state.customizada },
	{ value: 'espacamento', label: i18n.t('spacing') },
	{ value: 'materialGenetico', label: i18n.t('geneticMaterial') },
])

const dendrometros = computed(() => {
	return fazendasEscolhidas.value.length
		? dashboardStore.state.dashboardData.amostras.filter((f) =>
				fazendasEscolhidas.value.includes(f.fazendaId),
			)
		: dashboardStore.state.dashboardData.amostras
})

const areaFazendas = computed(() => {
	return fazendasEscolhidas.value.length
		? sumBy(
				dashboardStore.state.dashboardData.fazendas.filter((f) =>
					fazendasEscolhidas.value.includes(f.id),
				),
				(o) => o.area,
			)
		: sumBy(dashboardStore.state.dashboardData.fazendas, (o) => o.area)
})

const fazendasOptions = computed(() => {
	return dashboardStore.state.dashboardData.fazendas.map((f) => {
		return { key: f.id, value: f.nome, isActive: false }
	})
})

const fazendasAtivas = ref(
	fazendasOptions.value.map((m) => {
		return { value: m.value, key: m.key, isActive: true }
	}),
)

const fazendasEscolhidas = computed(() => {
	return fazendasAtivas.value.filter((f) => f.isActive).map((m) => m.key)
})

// const fazendas = computed({
//   get() {
//     return fazendasOptions.value;
//   },
//   set(value) {
//     fazendasEscolhidas.value = value
//       .filter((f) => f.isActive)
//       .map((m) => m.key);
//   },
// });

const areaPlantios = computed(() => {
	return fazendasEscolhidas.value.length
		? sumBy(
				dashboardStore.state.dashboardData.fazendas
					.filter((f) => fazendasEscolhidas.value.includes(f.id))
					.map((f) => f.dadosNivelPlantio)
					.flat(),
				(o) => o.area,
			)
		: sumBy(
				dashboardStore.state.dashboardData.fazendas
					.map((fazenda) => fazenda.dadosNivelPlantio)
					.flat(),
				(o) => o.area,
			)
})

const volumeMedioPorFazenda = computed(() => {
	return fazendasEscolhidas.value.length
		? mean(
				dashboardStore.state.dashboardData.fazendas
					.filter((f) => fazendasEscolhidas.value.includes(f.id))
					.map((m) =>
						mean(m.dadosNivelPlantio.map((m) => meanBy(m.medicoes, (o) => o.volumeComCasca))),
					),
			)
		: mean(
				dashboardStore.state.dashboardData.fazendas.map((fazenda) =>
					mean(
						fazenda.dadosNivelPlantio.map((plantio) =>
							meanBy(plantio.medicoes, (medicao) => medicao.volumeComCasca),
						),
					),
				),
			)
})

const plantios = computed(() => {
	return dashboardStore.state.dashboardData
		? dashboardStore.state.dashboardData.fazendas
				.map((fazenda) =>
					fazenda.dadosNivelPlantio.map((plantio) =>
						Object.assign(plantio, { fazenda: fazenda.fazenda }),
					),
				)
				.flat()
		: []
	// .filter(filtraPorFazenda)
	// .filter(filtraPorEspacamento)
	// .filter(filtraPorMaterialGenetico)
	// .filter(filtraPorPlantio);
})

const medicoes = computed(() => {
	let m = plantios.value
		.map((plantio) => plantio.medicoes.map((medicao) => Object.assign(medicao, plantio)))
		.flat()

	m.forEach((medicao) => delete medicao.medicoes)
	m = m.sort((a, b) =>
		new Date(a.dataMedicao).getTime() > new Date(b.dataMedicao).getTime() ? 1 : -1,
	)

	// Processa a propriedade customizada diretamente no computed
	const customizada = dashboardStore.state.customizada
	if (customizada) {
		m.forEach((medicao) => {
			// Se a variável escolhida for "Estrato", usa o estratoFlorestalId
			if (customizada === 'Estrato') {
				// Só mapeia se os estratos foram carregados
				if (estratosLoaded.value && estratosMap.value[medicao.estratoFlorestalId]) {
					medicao.customizada = estratosMap.value[medicao.estratoFlorestalId]
				} else {
					medicao.customizada = medicao.estratoFlorestalId
				}
			} else {
				// Caso contrário, busca na lista de variáveis customizadas
				medicao.customizada = medicao.variavelCustomizada?.find(
					(vc) => vc.name === customizada,
				)?.value
			}
		})
	}

	return m
})

const medicoesFazenda = computed(() => {
	return fazendasEscolhidas.value.length
		? medicoes.value.filter((f) => fazendasEscolhidas.value.includes(f.fazendaId))
		: medicoes.value
})

const medicoesAgrupadasPorCustomizada = computed(() => {
	return groupBy(medicoesFazenda.value, 'customizada')
})

const medicoesAgrupadasPorMaterialGenetico = computed(() => {
	return groupBy(medicoesFazenda.value, 'materialGenetico')
})

const medicoesAgrupadasPorEspacamento = computed(() => {
	return groupBy(medicoesFazenda.value, 'espacamento')
})

const agrupadas = computed(() => {
	return {
		customizada: medicoesAgrupadasPorCustomizada,
		materialGenetico: medicoesAgrupadasPorMaterialGenetico,
		espacamento: medicoesAgrupadasPorEspacamento,
	}
})

const volume = computed(() => {
	return orderBy(
		Object.keys(agrupadas.value[showBy.value].value).map((by) => {
			return [
				by,
				Number(meanBy(agrupadas.value[showBy.value].value[by], (o) => o[unidade.value]).toFixed(2)),
			]
		}),
		(o) => o[1],
		['desc'],
	)
})

const suffix = ref({
	dap: ' cm',
	ht: ' m',
	volumeComCasca: ' m³/ha',
})

const dataMedicoes = computed(() => {
	return [
		...new Set(
			sortBy(medicoes.value.map(({ dataMedicao }) => dataMedicao)),
			(o) => new Date(o).getTime(),
			['desc'],
		),
	].map((dataMedicao) => {
		return {
			value: dataMedicao,
			label: new Date(dataMedicao).toLocaleDateString(),
		}
	})
})

const ultimaMedicao = computed(() => {
	return dataMedicoes.value.length
		? orderBy(dataMedicoes.value, (o) => o.value, ['desc'])[0]
		: new Date().toISOString()
})

const chartOptions = computed(() => {
	return {
		chart: {
			type: 'area',
		},
		/**
		 * Creditos no canto inferior direito do grafico
		 */
		credits: {
			enabled: false,
		},
		/**
		 * Não usar exportacao padrao do highcharts
		 */
		exporting: {
			enabled: false,
		},
		/**
		 * Deasbilitar legendas
		 */
		legend: {
			enabled: false,
		},
		/**
		 * Opcoes na hora de plotar o grafico
		 */
		plotOptions: {
			line: {
				dataLabels: {
					enabled: true,
				},
			},
			series: {
				/**
				 * Animacao de 2 segundos
				 */
				animation: {
					duration: 1000,
				},
			},
		},
		/**
		 * Dados que devem ser colocados no grafico
		 */
		series: [
			{
				name: unidadeOptions.value.find((f) => f.value === unidade.value).label,
				type: 'column',
				color: '#72A313',
				data: volume.value,
				tooltip: {
					valueSuffix: suffix.value[unidade.value],
				},
			},
		],
		/**
		 * Titulo do grafico
		 */
		title: {
			text: '',
		},
		/**
		 * Balão ao passar o mouse sobre as barras
		 */
		// tooltip: {
		//   formatter: function () {
		//     let result = `
		//           <div class="container__graficofazenda__tooltip">
		//               <div class="container__graficofazenda__tooltip__fazenda">
		//                   <div class="container__graficofazenda__tooltip__fazenda__nome"> ${this.point.options.fazenda.nome}</div> <br/>
		//                   <div class="container__graficofazenda__tooltip__fazenda__info">
		//                     <span>Idade dos Plantios</span>
		//                     <span>${this.point.options.fazenda.idade} (ano)</span>
		//                   </div>
		//                   <div class="container__graficofazenda__tooltip__fazenda__info">
		//                     <span>Volume Total</span>
		//                     ${this.point.options.fazenda.volumeTotal} (m³)
		//                   </div>
		//                   <div class="container__graficofazenda__tooltip__fazenda__info">
		//                     <span>Volume Médio</span>
		//                     ${this.point.options.fazenda.volumeMedio} (m³/ha)
		//                   </div>
		//                   <div class="container__graficofazenda__tooltip__fazenda__info">
		//                     <span>Incremento Médio Anual</span>
		//                     ${this.point.options.fazenda.incrementoMedioAnual} (m³/ha/ano)
		//                   </div>
		//               </div>
		//           </div>`;

		//     return result;
		//   },
		//   backgroundColor: "#333647",
		//   borderRadius: 10,
		//   borderWidth: 0,
		//   outside: true,
		//   shadow: false,
		//   useHTML: true,
		// },
		/**
		 * Eixo X
		 */
		xAxis: {
			type: 'category',
			allowDecimals: false,
			title: {
				text: optionsShowBy.value.find((f) => f.value === showBy.value).label,
				style: {
					fontFamily: 'Open Sans, sans-serif',
					fontSize: '.8rem',
					fontWeight: 600,
				},
			},
			labels: {
				style: {
					color: '#01395e',
					fontFamily: 'Open Sans, sans-serif',
					fontSize: '.8rem',
					fontWeight: 700,
				},
			},
		},
		/**
		 * Eixo Y
		 */
		yAxis: [
			{
				// Primary yAxis
				labels: {
					format: `{value} ${suffix.value[unidade.value]}`,
					style: {
						// color: "#72A313"
					},
				},
				title: {
					text: '',
					// style: {
					//   color: Highcharts.getOptions().colors[1],
					// },
				},
			},
			// {
			//   // Secondary yAxis
			//   title: {
			//     text: "",
			//     style: {
			//       color: Highcharts.getOptions().colors[0],
			//     },
			//   },
			//   // labels: {
			//   //     format: '{value} mm',
			//   //     style: {
			//   //         color: Highcharts.getOptions().colors[0]
			//   //     }
			//   // },
			//   opposite: true,
			// },
			// {
			//   // Ternary yAxis
			//   title: {
			//     text: "",
			//     style: {
			//       color: Highcharts.getOptions().colors[2],
			//     },
			//   },
			//   // labels: {
			//   //   format: "{value} mm",
			//   //   style: {
			//   //     color: Highcharts.getOptions().colors[2],
			//   //   },
			//   // },
			//   opposite: true,
			// },
		],
	}
})

const variaveisCustomizadasOptions = computed(() => {
	const variaveisExistentes = dashboardStore.state.dashboardData
		? [
				...new Set(
					dashboardStore.state.dashboardData.fazendas
						.map((fazenda) => {
							return fazenda.dadosNivelPlantio
								.map((plantio) => {
									return plantio.variavelCustomizada || []
								})
								.flat()
						})
						.flat()
						.filter((m) => m !== null && m !== undefined)
						.map((m) => m.name),
				),
			]
		: []

	// Adiciona "Estrato" como opção se houver estratos carregados
	if (estratosLoaded.value && Object.keys(estratosMap.value).length > 0) {
		variaveisExistentes.push('Estrato')
	}

	return variaveisExistentes
})
</script>
